import { type MutableRefObject, type MouseEvent as ReactMouseEvent, useCallback } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { NewShapeTextSeedStyle } from '@/lib/shapes/types/new-shape-text-seed-style'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Double-click on a shape, routed to the edit it means: group isolation for a group, text edit
 * for a text frame, Text on a Path, crop for a photo, Edit Path for a path, or convert-to-path for
 * anything else.
 */
export function useShapeDoubleClick({
  internalShapeManagerRef,
  isTextToolActive,
  newShapeTextSeedStyle,
  enterGroupIsolationMode,
  tryEnterTextOnPathMode,
  tryEnterCropMode,
  tryEnterNodeEditMode,
  onConvertShapeToPath,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  isTextToolActive: boolean
  newShapeTextSeedStyle: NewShapeTextSeedStyle
  enterGroupIsolationMode: (groupId: string) => void
  tryEnterTextOnPathMode: (shape: ShapeData) => boolean
  tryEnterCropMode: (shape: ShapeData) => boolean
  tryEnterNodeEditMode: (shape: ShapeData, opts?: { clearSelectionWhenPen?: boolean }) => boolean
  onConvertShapeToPath: CenterCanvasConfig['onConvertShapeToPath']
}) {
  // Shared by both ShapeManager instances (base layer + the "above text" duplicate used
  // during shape-text editing) so their double-click behavior can never drift apart again.
  const handleShapeDoubleClick = useCallback(
    (shape: ShapeData, event: ReactMouseEvent) => {
      if (shape.kindId === 7) {
        enterGroupIsolationMode(shape.id)
        return
      }

      if (
        !isTextToolActive &&
        isShapeTextFrame(shape) &&
        shape.locked !== true
        // Note: frozen shapes intentionally allowed — freeze blocks geometry
        // gestures but not text content editing.
      ) {
        const shapeMgr = internalShapeManagerRef.current

        // Gated on frame *presence*, not content (WMPCM-8969), so a text-cleared frame lands here
        // rather than falling through to Edit Path. Such a frame keeps its own (empty) paragraphs,
        // so `enterShapeTextEdit` reuses their styling and the seed is only consulted for a frame
        // with no paragraphs at all — passing it keeps the two entry points symmetric.
        shapeMgr?.enterShapeTextEdit(shape.id, {
          caretClientPoint: { x: event.clientX, y: event.clientY },
          seedStyle: newShapeTextSeedStyle,
        })
        return
      }

      // An object that already has Text on a Path applied reopens it on double-click instead
      // of Edit Path / Clipping Path (WMPCM-9550 AC-12) — restoring the saved text, font,
      // direction and offset is `tryEnterTextOnPathMode`'s job via `createTextOnPathDraft`.
      // Takes priority over the photoFill/path branches below, so a photo or a path shape with
      // Text on a Path already applied reopens it rather than falling into Crop/Edit Path.
      //
      // Deliberately NOT gated on `shape.frozen` (WMPCM-7632): freeze blocks geometry gestures,
      // not content edits, and Text on a Path is a content edit — `shouldBlockShapeUpdate` lets a
      // `{ textOnPath }` patch through on a frozen shape because it touches neither
      // `touchesPlacementGeometry` nor `touchesPenPathGeometry`. The menus no longer gate on
      // frozen either, so excluding it here would leave double-click the one entry point that
      // refuses an object every other route accepts. `shape.locked` needs no check:
      // `handleShapeDoubleClickInternal` already refuses a locked shape before this handler runs,
      // and locked stays blocked on purpose (WMPCM-9562 AC-9).
      if (!isTextToolActive && shape.textOnPath != null) {
        tryEnterTextOnPathMode(shape)
        return
      }

      // Photo-filled shape (standalone or dropped into a hand-drawn shape): double-click
      // enters crop mode instead of node-edit / Edit Path — takes priority over the path
      // branch below, so a photo-filled pen path also crops rather than opening its nodes.
      if (shape.photoFill) {
        tryEnterCropMode(shape)
        return
      }

      if (shape.type === 'path' && shape.path) {
        // Note: a frozen path that is NOT a text frame is already turned away upstream in
        // handleShapeDoubleClickInternal (shape-manager/hooks/use-shape-click-handlers.ts). A frozen path text frame is let
        // through up there on purpose (WMPCM-6980) but takes the text branch above, so it only
        // lands here with the Type tool active — and `tryEnterNodeEditMode` refuses it on freeze.
        tryEnterNodeEditMode(shape, { clearSelectionWhenPen: true })
        return
      }

      // Non-path shape (e.g. a Shapes panel preset) with no editable text:
      // convert it to a path and enter node-edit mode, same as "Edit Path".
      onConvertShapeToPath?.(shape.id)
    },
    [
      enterGroupIsolationMode,
      isTextToolActive,
      newShapeTextSeedStyle,
      onConvertShapeToPath,
      tryEnterCropMode,
      tryEnterNodeEditMode,
      tryEnterTextOnPathMode,
      internalShapeManagerRef,
    ]
  )

  return { handleShapeDoubleClick }
}
